import React, { useState, useCallback, useEffect, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Header,
  SpaceBetween,
  ContentLayout,
} from "@cloudscape-design/components";
import { ROUTES } from "../../constants";
import { ConfigurableForm } from "../common/Forms";
import { mfrFormSchema } from "./schemas/mfr-form-schema";
import { createLeafSection } from "../../utils/documentSectionHelpers";
import { useFormStore } from "../../store/form-stores";
import { CURRENCY_CONFIG } from "../../constants/currencyConfig";
import { useGlobalStore } from "../../store/global-store";
import { useWorkflowStore } from "../../store/workflow-store";
import { useProductLineStaticFilter } from "../../hooks/useProductLineStaticFilter";
import { FIELD_EVENTS, SEARCH_CONFIG } from "../../constants/StaticFilterValues";
import { LoadingScreen } from "../common/UIStates";
import { logger } from "../../utils/logger";
import { PL_SLICE_OPTIONS, PL_DRILL_DOWN_OPTIONS } from "../../config/mfr-workflow-config";
import {ACTUALS_VALUE, MFR_TAG, USD_CONSTANT_VALUE, MILLIONS_VALUE} from "../../constants/mfr-constants";

export const MFRCreatePage: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isEditMode = Boolean(id);
  
  const { setFormData, getFormData } = useFormStore();
  const { selectedSpaceId, ensureSpaceSelected, fetchSpaces } = useGlobalStore();
  const { workflows, workflowTemplates, fetchWorkflows, fetchWorkflowTemplates, createWorkflow, updateWorkflow } = useWorkflowStore();
  
  const [isLoadingWorkflow, setIsLoadingWorkflow] = useState(isEditMode);
  
  // Fetch spaces and workflow templates on mount
  useEffect(() => {
    fetchSpaces();
  }, [fetchSpaces]);

  useEffect(() => {
    if (selectedSpaceId) {
      fetchWorkflowTemplates(selectedSpaceId);
      if (isEditMode) {
        fetchWorkflows(selectedSpaceId);
      }
    }
  }, [selectedSpaceId, fetchWorkflowTemplates, fetchWorkflows, isEditMode]);

  // Load workflow data for edit mode
  useEffect(() => {
    if (isEditMode && id && workflows.length > 0) {
      const workflow = workflows.find(w => w.id === id);
      if (workflow) {
        populateFormFromWorkflow(workflow);
        setIsLoadingWorkflow(false);
      } else {
        setIsLoadingWorkflow(false);
      }
    }
  }, [isEditMode, id, workflows]);

  // Function to populate form from workflow data
  const populateFormFromWorkflow = (workflow: any) => {
    const formData: any = {
      name: workflow.name || '',
      description: workflow.description || '',
      marketplace: workflow.input?.marketplace || [],
      channels: workflow.input?.channels || [],
      product_lines: workflow.input?.product_lines || '',
      periods: workflow.input?.periods || '',
      base_scenario: workflow.input?.base_scenario || ACTUALS_VALUE,
      comparison_scenario: workflow.input?.comparison_scenario || [],
      currency: workflow.input?.currency || USD_CONSTANT_VALUE,
    };

    // Populate document sections from workflow steps
    const documentSections: any = {};
    const plSliceFilters: string[] = [];
    const plDrillDownFilters: string[] = [];
    const plSliceChildren: any[] = [];
    const plDrillDownChildren: any[] = [];

    if (workflow.steps) {
      workflow.steps.forEach((step: any) => {
        documentSections[step.id] = {
          includeTable: step.input?.include_table_generation || false,
          includeCommentary: step.input?.include_commentary_generation || false,
          currency: step.input?.currency || USD_CONSTANT_VALUE
        };

        // Extract P&L By GL filter from the 'executive_pnl_pnl_by_gl' step
        if (step.id === 'executive_pnl_pnl_by_gl') {
          documentSections['executive_pnl_pnl_by_gl'] = {
            includeTable: step.input?.include_table_generation || false,
            includeCommentary: step.input?.include_commentary_generation || false,
            currency: step.input?.currency || USD_CONSTANT_VALUE,
            ...(step.input?.product_lines && { filterValue: step.input.product_lines })
          };
        }

        // Check if this step belongs to dynamic sections

        const plDrillDownOptions = PL_DRILL_DOWN_OPTIONS;

        // Check if step is a P&L Slice dynamic section
        const sliceOption = PL_SLICE_OPTIONS.find(opt => opt.value === step.id);
        if (sliceOption) {
          plSliceFilters.push(step.id);
          plSliceChildren.push(createLeafSection(step.id, sliceOption.label, {
            includeTable: step.input?.include_table_generation ?? false,
            includeCommentary: step.input?.include_commentary_generation ?? false,
            currency: step.input?.currency || MILLIONS_VALUE
          }));
        }

        // Check if step is a P&L Drill Down dynamic section
        const drillDownOption = plDrillDownOptions.find(opt => opt.value === step.id);
        if (drillDownOption) {
          plDrillDownFilters.push(step.id);
          plDrillDownChildren.push(createLeafSection(step.id, drillDownOption.label, {
            includeTable: step.input?.include_table_generation ?? false,
            includeCommentary: step.input?.include_commentary_generation ?? false,
            currency: step.input?.currency || MILLIONS_VALUE
          }));
        }
      });
    }

    // Set filter values in documentSections for the table component
    if (plSliceFilters.length > 0) {
      documentSections['pl-slice'] = { filterValue: plSliceFilters };
    }
    if (plDrillDownFilters.length > 0) {
      documentSections['pl-drill-down'] = { filterValue: plDrillDownFilters };
    }

    formData.documentSections = documentSections;

    // Set form data
    setFormData(mfrFormSchema.id, formData);
    
    // Load GL options if product_lines is selected
    if (formData.product_lines) {
      loadGLOptionsForProductLine(formData.product_lines);
    }
    
    // Set dynamic options for children
    setDynamicOptions(prev => ({
      ...prev,
      ...(plSliceChildren.length > 0 && { 'documentSections_pl-slice_children': plSliceChildren }),
      ...(plDrillDownChildren.length > 0 && { 'documentSections_pl-drill-down_children': plDrillDownChildren })
    }));
    logger.log('Populated form data from workflow:', formData);
  };
  
  // Get MFR template ID from workflow templates
  const mfrTemplateId = workflowTemplates?.find(
    workflow => workflow.tags?.includes("MFR")
  )?.id;
  
  // State for dynamic dropdown options
  const [dynamicOptions, setDynamicOptions] = useState<Record<string, any>>({});
  
  // State to track all nodes that have been loaded/expanded for GL options
  const [allLoadedNodes, setAllLoadedNodes] = useState<Map<string, any>>(new Map());
  
  // Track which specific node is loading
  const [loadingNodes, setLoadingNodes] = useState<Set<string>>(new Set());
  
  // Track GL options loading state
  const [isGLOptionsLoading, setIsGLOptionsLoading] = useState<boolean>(false);
  
  // Search state for ProductLine
  const [isSearchLoading, setIsSearchLoading] = useState(false);
  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);

  // Initialize filter hooks
  const { loadFilterData } = useProductLineStaticFilter((error: Error) => {
    console.error('Filter loading error:', error);
  });

  // Cleanup form data on component unmount
  useEffect(() => {
    return () => {
      setFormData(mfrFormSchema.id, {});
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [setFormData]);

  // Handle cancellation
  const handleCancel = () => {
    if (
      confirm(
        `Are you sure you want to cancel? Any unsaved changes will be lost.`
      )
    ) {
      // Clear all form data from store
      setFormData(mfrFormSchema.id, {});
      navigate(ROUTES.REPORTS_ALL);
    }
  };

  // Handle Save action
  const handleSave = async (data: any) => {
    console.log("MFR form saved:", {
      formData: data,
      timestamp: new Date().toISOString(),
    });

    try {
      const spaceId = await ensureSpaceSelected();
      // Build steps from document sections
      let steps: Array<{id: string, input: Record<string, any>}> = [];
      const documentSections = data.documentSections || {};
      
      Object.entries(documentSections).forEach(([sectionId, sectionData]: [string, any]) => {
        // Skip parent sections that have dynamic children
        if (sectionId === 'pl-slice' || sectionId === 'pl-drill-down') {
          return;
        }
        // Only include sections that have actual configuration
        if (sectionData.includeTable || sectionData.includeCommentary) {
          // Check if this is a dynamic child and get its defaults
          const allDynamicChildren = [
            ...(dynamicOptions['documentSections_pl-slice_children'] || []),
            ...(dynamicOptions['documentSections_pl-drill-down_children'] || [])
          ];
          const dynamicChild = allDynamicChildren.find(child => child.id === sectionId);
          
          const stepInput: any = {
            include_table_generation: sectionData.includeTable !== undefined ? sectionData.includeTable : (dynamicChild?.includeTable || false),
            include_commentary_generation: sectionData.includeCommentary !== undefined ? sectionData.includeCommentary : (dynamicChild?.includeCommentary || false),
            currency: sectionData.currency || dynamicChild?.currency || MILLIONS_VALUE
          };

          // Add product_line_filter for P&L by GL step if GL filter is selected
          if (sectionId === 'executive_pnl_pnl_by_gl' && documentSections['executive_pnl_pnl_by_gl']?.filterValue) {
            stepInput.product_lines = documentSections['executive_pnl_pnl_by_gl'].filterValue;
          }

          steps.push({
            id: sectionId,
            input: stepInput
          });
        }
      });

      // Helper function to process dynamic children
      const processChildren = (childrenKey: string, logPrefix: string, documentSections: Record<string, any>, defaultCurrency: string) => {
        const children = dynamicOptions[childrenKey] || [];
        
        // For updates: Remove existing dynamic children of this specific type that are no longer selected
        if (isEditMode) {
          const childIds = children.map((child: any) => child.id);
          const isSliceType = childrenKey.includes('pl-slice');
          const relevantOptions = isSliceType ? PL_SLICE_OPTIONS : PL_DRILL_DOWN_OPTIONS;
          
          steps = steps.filter(step => {
            // Keep non-dynamic steps and steps from other dynamic types
            const isThisTypeChild = relevantOptions.some(opt => opt.value === step.id);
            return !isThisTypeChild || childIds.includes(step.id);
          });
        }

        children.forEach((child: any) => {
          // Skip if this child is already processed from workflow steps
          if (steps.some(step => step.id === child.id)) {
            return;
          }
          
          const childData = documentSections[child.id] || {};

          if (child.includeTable || child.includeCommentary) {
            steps.push({
              id: child.id,
              input: {
                include_table_generation: childData.includeTable !== undefined ? childData.includeTable : child.includeTable,
                include_commentary_generation: childData.includeCommentary !== undefined ? childData.includeCommentary : child.includeCommentary,
                currency: childData.currency !== undefined ? childData.currency : child.currency
              }
            });
          }
        });
      };

      // Process dynamic children from P&L by Slice and P&L Drill Down
      processChildren('documentSections_pl-slice_children', 'pl-slice', documentSections, data.currency);
      processChildren('documentSections_pl-drill-down_children', 'pl-drill-down', documentSections, data.currency);

      // Build CreateWorkflowRequest from form data
      const workflowRequest = {
        name: data.name,
        description: data.description,
        parentId: mfrTemplateId,
        input: {
          product_lines: data.product_lines,
          marketplace: data.marketplace,
          currency: data.currency,
          periods: data.periods,
          base_scenario: data.base_scenario,
          comparison_scenario: data.comparison_scenario,
          channels: data.channels
        },
        steps: steps,
        tags: [MFR_TAG]
      };

      // Call the appropriate API based on edit mode
      let response;
      if (isEditMode && id) {
        response = await updateWorkflow(spaceId, id, { 
          spaceId, 
          workflowId: id, 
          workflow: workflowRequest 
        });
      } else {
        response = await createWorkflow({ spaceId, workflow: workflowRequest });
      }
      
      logger.log(`Workflow ${isEditMode ? 'updated' : 'created'} successfully:`, response);
      // TODO: Update with Notification banner
      alert(`MFR Report "${data.name || "Untitled"}" ${isEditMode ? 'updated' : 'saved'} successfully!`);

      // Clear form data after successful save
      setFormData(mfrFormSchema.id, {});
      
      // Navigate to MFR workflows page
      navigate(ROUTES.MFR_VIEW);
      
    } catch (error: any) {
      logger.error("Failed to save MFR report:", error);
      // TODO: Update with Notification banner
      alert(`Failed to ${isEditMode ? 'update' : 'save'} MFR Report: ${error?.message || 'Unknown error'}`);
    }
  };

  /**
   * Handles ProductLine search functionality with debouncing
   */
  const handleProductLineSearch = useCallback(async (searchValue: string, filterType: string) => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    // Show loading immediately when typing
    if (searchValue && searchValue.trim().length >= 1) {
      setIsSearchLoading(true);
    }

    searchTimeoutRef.current = setTimeout(async () => {
      if (!searchValue || searchValue.trim().length < SEARCH_CONFIG.MIN_SEARCH_LENGTH) {
        setIsSearchLoading(false);
        
        // Reset to default tree data
        const product_linesField = mfrFormSchema.sections[1].fields.find(f => f.name === 'product_lines') as any;
        const defaultTreeData = product_linesField?.treeData || [];
        
        setDynamicOptions(prev => ({
          ...prev,
          product_lines: defaultTreeData
        }));
        
        return;
      }
      
      try {
        abortControllerRef.current = new AbortController();
        
        // Clear current tree data
        setDynamicOptions(prev => ({
          ...prev,
          productLine: []
        }));

        const response = await loadFilterData(
          filterType,
          { key: searchValue },
          undefined,
          abortControllerRef.current.signal
        );
        
        if (response && response.children) {
          setDynamicOptions(prev => ({
            ...prev,
            product_lines: response.children
          }));
        } else {
          setDynamicOptions(prev => ({
            ...prev,
            product_lines: []
          }));
        }
        
      } catch (error) {
        if ((error as Error).name === 'AbortError') {
          return;
        }
        console.error('Error searching product lines', error);
        setDynamicOptions(prev => ({
          ...prev,
          product_lines: []
        }));
      } finally {
        setIsSearchLoading(false);
      }
    }, SEARCH_CONFIG.DEBOUNCE_DELAY);
  }, [loadFilterData, mfrFormSchema.sections, dynamicOptions.product_lines]);

  /**
   * Handles ProductLine dynamic node loading
   */
  const handleProductLineLoad = useCallback(async (node: any, filterType: string) => {
    try {
      // Track this specific node as loading
      const newLoadingNodes = new Set(loadingNodes);
      newLoadingNodes.add(node.key || node.value);
      setLoadingNodes(newLoadingNodes);
      
      const data = await loadFilterData(filterType, node, node.hierarchyType);
      
      if (data && data.children) {
        // Store this node and its children in our tracking map
        setAllLoadedNodes(prev => {
          const newMap = new Map(prev);
          newMap.set(node.key || node.value, {
            ...node,
            children: data.children
          });
          return newMap;
        });
        
        // Get current tree data from schema or dynamic options
        const product_linesField = mfrFormSchema.sections[1].fields.find(f => f.name === 'product_lines') as any;
        const currentTreeData = dynamicOptions.product_lines || product_linesField?.treeData || [];
        
        // Update the tree data by adding children to the expanded node
        const updatedTreeData = updateTreeNodeChildren(currentTreeData, node, data.children);
        
        setDynamicOptions(prev => ({
          ...prev,
          product_lines: updatedTreeData
        }));
      }
    } catch (error) {
      console.error('Failed to load filter data:', error);
    } finally {
      // Remove this node from loading set
      const newLoadingNodes = new Set(loadingNodes);
      newLoadingNodes.delete(node.key || node.value);
      setLoadingNodes(newLoadingNodes);
    }
  }, [loadFilterData, loadingNodes, mfrFormSchema.sections, dynamicOptions.product_lines]);

  /**
   * Loads GL options for the selected product line by fetching its immediate children
   */
  const loadGLOptionsForProductLine = useCallback(async (selectedValue: string) => {
    setIsGLOptionsLoading(true);
    
    try {
      // First check if we have this node in our loaded nodes map
      let selectedNode = allLoadedNodes.get(selectedValue);
      
      if (!selectedNode) {
        // If not in loaded nodes, search in current tree data
        const product_linesField = mfrFormSchema.sections[1].fields.find(f => f.name === 'product_lines') as any;
        const currentTreeData = dynamicOptions.product_lines || product_linesField?.treeData || [];
        
        const findNodeInTree = (nodes: any[], value: string): any => {
          for (const node of nodes) {
            if (node.value === value || node.key === value) {
              return node;
            }
            if (node.children && node.children.length > 0) {
              const found = findNodeInTree(node.children, value);
              if (found) return found;
            }
          }
          return null;
        };
        
        selectedNode = findNodeInTree(currentTreeData, selectedValue);
      }
      
      if (!selectedNode) {
        console.warn('Selected product line node not found:', selectedValue);
        return;
      }
      
      // Check if this is a leaf node (no children expected)
      if (selectedNode.isLeaf === true) {
        // Show the parent node itself as option
        const glOptions = [{
          value: selectedNode.value,
          label: selectedNode.title
        }];
        
        setDynamicOptions(prev => ({ 
          ...prev, 
          'documentSections_executive_pnl_pnl_by_gl': glOptions 
        }));
        return;
      }
      
      // Check if we already have children for this node
      if (selectedNode.children && selectedNode.children.length > 0) {
        const glOptions = selectedNode.children.map((child: any) => ({
          value: child.value,
          label: child.title
        }));
        
        setDynamicOptions(prev => ({ 
          ...prev, 
          'documentSections_executive_pnl_pnl_by_gl': glOptions 
        }));
        return;
      }
      
      // For GL options, we need to get children
      // The API expects the level of children we want, not the parent level
      let childLevel;
      if (selectedNode.level !== undefined) {
        childLevel = selectedNode.level + 1;
      } else {
        // Root node - children are at level 1  
        childLevel = 1;
      }
      
      // But if this node was loaded from expansion, it might not have the correct level
      // Let's also try to determine level from the tree position
      const determineNodeLevel = (nodes: any[], targetValue: string, currentLevel = 0): number => {
        for (const node of nodes) {
          if (node.value === targetValue) {
            return currentLevel;
          }
          if (node.children && node.children.length > 0) {
            const found = determineNodeLevel(node.children, targetValue, currentLevel + 1);
            if (found !== -1) return found;
          }
        }
        return -1;
      };
      
      const product_linesField = mfrFormSchema.sections[1].fields.find(f => f.name === 'product_lines') as any;
      const currentTreeData = dynamicOptions.product_lines || product_linesField?.treeData || [];
      const actualLevel = determineNodeLevel(currentTreeData, selectedValue);
      
      // Use the actual level + 1 for children
      const finalChildLevel = actualLevel >= 0 ? actualLevel + 1 : childLevel;
      
      const nodeForChildren = {
        ...selectedNode,
        level: finalChildLevel
      };
      
      // Load children of selected node
      const data = await loadFilterData('productid', nodeForChildren, selectedNode.hierarchyType);
      
      if (data && data.children && data.children.length > 0) {
        // Store the node with its children for future use
        setAllLoadedNodes(prev => {
          const newMap = new Map(prev);
          newMap.set(selectedValue, {
            ...selectedNode,
            children: data.children
          });
          return newMap;
        });
        
        // Transform children to flat dropdown options (no tree structure)
        const glOptions = data.children.map((child: any) => ({
          value: child.value,
          label: child.title
        }));
        
        setDynamicOptions(prev => ({ 
          ...prev, 
          'documentSections_executive_pnl_pnl_by_gl': glOptions 
        }));
      } else {
        // No children found - show the parent node itself as option
        const glOptions = [{
          value: selectedNode.value,
          label: selectedNode.title
        }];
        
        setDynamicOptions(prev => ({ 
          ...prev, 
          'documentSections_executive_pnl_pnl_by_gl': glOptions 
        }));
      }
    } catch (error) {
      console.error('Failed to load GL options for product line:', error);
      // Set empty options on error
      setDynamicOptions(prev => ({ 
        ...prev, 
        'documentSections_executive_pnl_pnl_by_gl': [] 
      }));
    } finally {
      setIsGLOptionsLoading(false);
    }
  }, [loadFilterData, allLoadedNodes, mfrFormSchema.sections, dynamicOptions.product_lines]);
  // MFR-specific field change handler with cascading logic
  const handleFieldChange = useCallback(async (fieldName: string, value: any) => {
    console.log(`MFR field "${fieldName}" changed:`, value);
    
    if (fieldName === `product_lines${FIELD_EVENTS.SEARCH}`) {
      const { searchValue, filterType } = value;
      await handleProductLineSearch(searchValue, filterType);
      return;
    }
    
    if (fieldName === `product_lines${FIELD_EVENTS.LOAD_DATA}`) {
      const { node, filterType } = value;
      await handleProductLineLoad(node, filterType);
      return;
    }
    
    // Handle P&L by Slice filter selection
    if (fieldName === 'documentSections_pl-slice_filter') {
      const selectedOptions = value || [];
      // Create leaf sections for all selected options
      const newChildren = selectedOptions.map((optionValue: string) => {
        const option = PL_SLICE_OPTIONS.find(opt => opt.value === optionValue);
        return createLeafSection(optionValue, option?.label || optionValue, { 
          includeTable: true,
          includeCommentary: false,
          currency: MILLIONS_VALUE
        });
      });
      
      // Update dynamic options to reflect the new children
      setDynamicOptions(prev => ({
        ...prev,
        'documentSections_pl-slice_children': newChildren
      }));
      return;
    }
    
    // Handle P&L Drill Down filter selection
    if (fieldName === 'documentSections_pl-drill-down_filter') {
      const selectedOptions = value || [];
      
      // Create leaf sections for all selected options (using label for display)
      const newChildren = selectedOptions.map((optionValue: string) => {
        const option = PL_DRILL_DOWN_OPTIONS.find(opt => opt.value === optionValue);
        const label = option ? option.label : optionValue;
        return createLeafSection(optionValue, label, { 
          includeTable: true,
          includeCommentary: false,
          currency: MILLIONS_VALUE
        });
      });
      
      // Update dynamic options to reflect the new children
      setDynamicOptions(prev => ({
        ...prev,
        'documentSections_pl-drill-down_children': newChildren
      }));
      return;
    }
    
    // Handle MFR-specific cascading logic
    if (fieldName === 'product_lines') {
      // Reset P&L by GL filter selections when product line changes
      // Clear the dynamic options first to reset the UI
      setDynamicOptions(prev => ({
        ...prev,
        'documentSections_executive_pnl_pnl_by_gl': [],
        'documentSections_executive_pnl_pnl_by_gl_reset': Date.now() // Trigger reset
      }));
      
      // Load GL options from API based on selected product line
      await loadGLOptionsForProductLine(value);
    }
    
    // Future MFR cascading dependencies can be added here, e.g.:
    // if (fieldName === 'marketplace') {
    //   const regionOptions = getRegionOptionsForMarketplace(value);
    //   setDynamicOptions(prev => ({ ...prev, 'region': regionOptions }));
    // }
  }, [handleProductLineSearch, handleProductLineLoad, loadGLOptionsForProductLine]);

  // Helper function to update tree node children
  const updateTreeNodeChildren = (treeData: any[], targetNode: any, children: any[]): any[] => {
    return treeData.map(node => {
      if (node.key === targetNode.key || node.value === targetNode.value) {
        return {
          ...node,
          children: children.map((child: any) => ({
            value: child.value || child.key,
            title: child.title || child.label || child.name,
            key: child.key || child.value,
            isLeaf: child.isLeaf !== false,
            hierarchyType: node.hierarchyType // Inherit hierarchy from parent
          }))
        };
      }
      if (node.children && node.children.length > 0) {
        return {
          ...node,
          children: updateTreeNodeChildren(node.children, targetNode, children)
        };
      }
      return node;
    });
  };

  // Get initial form data with default values
  const getInitialValues = () => {
    const savedData = getFormData(mfrFormSchema.id);

    // Apply default values from schema
    const defaultValues: any = {};
    mfrFormSchema.sections.forEach((section) => {
      section.fields.forEach((field) => {
        if (field.defaultValue !== undefined) {
          defaultValues[field.name] = field.defaultValue;
        }
        
        // Handle documentSectionsTable field type - initialize default values for table items
        if (field.type === 'documentSectionsTable') {
          const tableField = field as any;
          const tableData = tableField.tableData || [];
          const documentSectionsDefaults: any = {};
          
          // Helper function to traverse tree and initialize leaf nodes
          const initializeLeafNodes = (nodes: any[]) => {
            nodes.forEach((node: any) => {
              if (node.children && node.children.length > 0) {
                // This is a parent node, traverse its children
                initializeLeafNodes(node.children);
              } else {
                // This is a leaf node, initialize its defaults
                documentSectionsDefaults[node.id] = {
                  includeTable: node.includeTable ?? false,
                  includeCommentary: node.includeCommentary ?? false,
                  currency: node.currency ?? CURRENCY_CONFIG.DEFAULT
                };
              }
            });
          };
          
          // Initialize defaults for all leaf nodes in tree
          initializeLeafNodes(tableData);
          
          defaultValues[field.name] = documentSectionsDefaults;
        }
      });
    });

    return { ...defaultValues, ...savedData };
  };

  // Show loading screen while fetching workflow data in edit mode
  if (isLoadingWorkflow) {
    return <LoadingScreen message="Loading workflow data..." />;
  }

  return (
    <ContentLayout
      defaultPadding
      header={
        <Header
          variant="h1"
          description={isEditMode 
            ? "Edit your MFR report configuration and document sections"
            : "Configure your MFR report with the required filters and document sections"
          }
        >
          {isEditMode ? 'Edit MFR Report' : 'Create MFR Report'}
        </Header>
      }
    >
      <SpaceBetween size="l">
        {/* The Configurable Form with custom action buttons */}
        <ConfigurableForm
          workflowId={mfrFormSchema.id}
          schema={mfrFormSchema}
          initialValues={getInitialValues()}
          onFieldChange={handleFieldChange}
          dynamicOptions={{
            ...dynamicOptions,
            product_lines_loadingNodes: loadingNodes,
            product_lines_searchLoading: isSearchLoading,
            'documentSections_executive_pnl_pnl_by_gl_loading': isGLOptionsLoading
          } as any}
          showHeader={false}
          actionButtons={[
            { text: 'Cancel', variant: 'normal', position: 'left', handler: handleCancel, bypassValidation: true },
            { text: isEditMode ? 'Update' : 'Save', variant: 'primary', position: 'right', handler: handleSave }
          ]}
        />
      </SpaceBetween>
    </ContentLayout>
  );
};
